import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import {
  TrendingUp,
  AccountBalance,
  Category,
  Inventory,
  FiberManualRecord,
  Search as SearchIcon,
  Close as CloseIcon,
  WarningAmber,
  PieChartOutline,
  BarChart as BarChartIcon,
  History,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { dashboardAPI, siteAPI } from "../services/api";
import { getTopMaterialsByDepartment } from "../services/settingsAPI";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const DashboardHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSupervisor = user?.role === "supervisor";

  const [stats, setStats] = useState(null);
  const [spendingBySite, setSpendingBySite] = useState([]);
  const [topMaterials, setTopMaterials] = useState({
    civil: [],
    electrical: [],
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [materialSearch, setMaterialSearch] = useState("");
  const [activitySearch, setActivitySearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [finalBoqs, setFinalBoqs] = useState([]);
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  useEffect(() => {
    fetchDashboardData();
    fetchSites();
    if (!isSupervisor) {
      fetchFinalBoqs();
      fetchBudgetAlerts();
    }
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      fetchTopMaterials(selectedSite || null);
    }
  }, [selectedSite]);

  const fetchSites = async () => {
    try {
      const response = await siteAPI.getAll();
      setSites(response.data.data?.sites || []);
    } catch (err) {
      console.error("Error fetching sites:", err);
    }
  };

  const fetchTopMaterials = async (siteId) => {
    try {
      const response = await getTopMaterialsByDepartment(10, siteId);
      setTopMaterials({
        civil: response.data?.civil || [],
        electrical: response.data?.electrical || [],
      });
    } catch (err) {
      console.error("Error fetching top materials:", err);
    }
  };

  const fetchFinalBoqs = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1"}/boqs/unified-summary`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      const data = await res.json();
      const boqs = data.data?.boqs || [];
      const finals = boqs.filter((b) => b.status === "final");
      setFinalBoqs(finals);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBudgetAlerts = async () => {
    try {
      const response = await siteAPI.getAll();
      const sitesData = response.data.data?.sites || [];
      const alerts = sitesData
        .filter((site) => {
          const spending = parseFloat(site.actualSpending) || 0;
          const budget = parseFloat(site.projectedBudget) || 0;
          if (budget <= 0) return false;
          const pct = (spending / budget) * 100;
          return pct >= (parseFloat(site.budgetAlertThreshold) || 80);
        })
        .map((site) => {
          const spending = parseFloat(site.actualSpending) || 0;
          const budget = parseFloat(site.projectedBudget);
          const pct = (spending / budget) * 100;
          return {
            id: site.id,
            siteName: site.siteName,
            location: site.location,
            pct,
            spending,
            budget,
            isOver: pct >= 100,
          };
        })
        .sort((a, b) => b.pct - a.pct);
      setBudgetAlerts(alerts);
    } catch (e) {
      console.error("Budget alerts error:", e);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      if (isSupervisor) {
        // Supervisor-specific dashboard
        const response = await dashboardAPI.getSupervisorDashboard();
        const data = response.data.data;

        setStats(data.stats || {});
        setSpendingBySite(data.sites || []);
        setRecentActivities(data.recentActivities || []);
      } else {
        // Admin/Manager dashboard
        const [statsRes, spendingRes, activitiesRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getSpendingBySite(),
          dashboardAPI.getRecentActivities(8),
        ]);

        setStats(statsRes.data.data);
        setSpendingBySite(spendingRes.data.data?.sites || []);
        setRecentActivities(activitiesRes.data.data?.activities || []);
      }

      // Fetch top materials after sites are loaded
      await fetchTopMaterials(null);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "4px solid",
        borderLeftColor: color,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
          transform: "translateY(-3px)",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ fontSize: "0.85rem", fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{ my: 1.5, color: "text.primary" }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}15`,
            }}
          >
            <Icon sx={{ fontSize: 36, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Prepare chart data
  const totalCivilSpending = spendingBySite.reduce(
    (sum, site) => sum + (site.civilSpending || 0),
    0,
  );
  const totalElectricalSpending = spendingBySite.reduce(
    (sum, site) => sum + (site.electricalSpending || 0),
    0,
  );

  const departmentData = [
    {
      name: "Civil",
      value: totalCivilSpending || 1,
      display: `₹${(totalCivilSpending / 10000000).toFixed(2)}Cr`,
    },
    {
      name: "Electrical",
      value: totalElectricalSpending || 1,
      display: `₹${(totalElectricalSpending / 10000000).toFixed(2)}Cr`,
    },
  ];

  const COLORS = {
    Civil: "#1F8F6E",
    Electrical: "#C4472C",
  };

  // Site spending bar chart data
  const siteSpendingData = spendingBySite
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 6)
    .map((site) => ({
      name:
        site.siteName.length > 12
          ? site.siteName.substring(0, 12) + "..."
          : site.siteName,
      spending: site.totalSpent / 100000,
      fullName: site.siteName,
    }));

  const filteredCivil = topMaterials.civil.filter(
    (m) =>
      !materialSearch.trim() ||
      m.materialName?.toLowerCase().includes(materialSearch.toLowerCase()) ||
      m.unit?.toLowerCase().includes(materialSearch.toLowerCase()),
  );

  const filteredElectrical = topMaterials.electrical.filter(
    (m) =>
      !materialSearch.trim() ||
      m.materialName?.toLowerCase().includes(materialSearch.toLowerCase()) ||
      m.unit?.toLowerCase().includes(materialSearch.toLowerCase()),
  );

  const getActivityColor = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("payment")) return "#4F46E5";
    if (t.includes("received")) return "#1F8F6E";
    if (t.includes("issued") || t.includes("used")) return "#C6791E";
    return "#9E9E9E";
  };

    const totalDeptSpending = totalCivilSpending + totalElectricalSpending;
  const biggerDept =
    totalElectricalSpending >= totalCivilSpending ? "Electrical" : "Civil";
  const biggerDeptPct =
    totalDeptSpending > 0
      ? (
          (Math.max(totalElectricalSpending, totalCivilSpending) /
            totalDeptSpending) *
          100
        ).toFixed(1)
      : "0";
  const topSpendingSite = [...spendingBySite].sort(
    (a, b) => b.totalSpent - a.totalSpent,
  )[0];

  const filteredActivities = recentActivities.filter(

    (a) =>
      !activitySearch.trim() ||
      a.siteName?.toLowerCase().includes(activitySearch.toLowerCase()) ||
      a.materialName?.toLowerCase().includes(activitySearch.toLowerCase()) ||
      a.type?.toLowerCase().includes(activitySearch.toLowerCase()) ||
      a.action?.toLowerCase().includes(activitySearch.toLowerCase()),
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSupervisor
            ? "Overview of your assigned sites"
            : "Overview of your material tracking system"}
        </Typography>
      </Box>

      {/* Dashboard Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{ mb: 3, minHeight: 0, "& .MuiTabs-indicator": { display: "none" } }}
      >
        <Tab
          value="overview"
          label="Overview"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            minHeight: 0,
            borderRadius: 999,
            mr: 1,
            px: 2,
            py: 0.75,
            border: "1px solid",
            borderColor: activeTab === "overview" ? "#1A1A17" : "divider",
            bgcolor: activeTab === "overview" ? "#1A1A17" : "transparent",
            color: activeTab === "overview" ? "#FFFFFF !important" : "text.primary",
          }}
        />
        <Tab
          value="sites"
          label="Sites"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            minHeight: 0,
            borderRadius: 999,
            px: 2,
            py: 0.75,
            border: "1px solid",
            borderColor: activeTab === "sites" ? "#1A1A17" : "divider",
            bgcolor: activeTab === "sites" ? "#1A1A17" : "transparent",
            color: activeTab === "sites" ? "#FFFFFF !important" : "text.primary",
          }}
        />
      </Tabs>

      {activeTab === "overview" && (
      <>
      {/* Insight Strip — admin/manager only */}
      {!isSupervisor && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#4F46E5", mb: 1 }}>
                Spend Driver
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                <strong>{biggerDept}</strong> work is driving <strong>{biggerDeptPct}%</strong> of total spend this period.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#C4472C", mb: 1 }}>
                Budget Risk
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {budgetAlerts.length === 0 ? (
                  "All sites are within budget — nothing needs attention right now."
                ) : (
                  <>
                    <strong>{budgetAlerts.length}</strong> site{budgetAlerts.length > 1 ? "s" : ""} need attention —{" "}
                    <strong>{budgetAlerts[0].siteName}</strong> is most urgent at {budgetAlerts[0].pct.toFixed(1)}%.
                  </>
                )}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#C6791E", mb: 1 }}>
                Busiest Site
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {topSpendingSite ? (
                  <>
                    <strong>{topSpendingSite.siteName}</strong> leads spending so far at{" "}
                    <strong>₹{(topSpendingSite.totalSpent / 100000).toFixed(1)}L</strong>.
                  </>
                ) : (
                  "No spending data yet."
                )}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Budget Alert Banner — admin/manager only */}
      {!isSupervisor && budgetAlerts.length > 0 && (
        <Box
          sx={{
            mb: 3,
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid #fecaca",
            boxShadow: "0 2px 8px rgba(220,38,38,0.08)",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg,#dc2626,#b91c1c)",
              px: 3,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{ color: "white", fontWeight: 700, fontSize: "0.95rem" }}
            >
              🚨 Budget Alerts — {budgetAlerts.length} site
              {budgetAlerts.length > 1 ? "s" : ""} need attention
            </Typography>
          </Box>
          <Box sx={{ background: "white" }}>
            {budgetAlerts.map((alert, idx) => (
              <Box
                key={alert.id}
                onClick={() => navigate(`/sites/${alert.id}?tab=1`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 3,
                  py: 1.5,
                  borderBottom:
                    idx < budgetAlerts.length - 1
                      ? "1px solid #fee2e2"
                      : "none",
                  flexWrap: "wrap",
                  gap: 1,
                  cursor: "pointer",
                  "&:hover": { background: "#fef2f2" },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#0f172a",
                    }}
                  >
                    {alert.siteName}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    📍 {alert.location}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>
                    ₹{alert.spending.toLocaleString("en-IN")} / ₹
                    {alert.budget.toLocaleString("en-IN")}
                  </Typography>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 99,
                      background: alert.isOver ? "#fef2f2" : "#fffbeb",
                      border: `1px solid ${alert.isOver ? "#fca5a5" : "#fde68a"}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: alert.isOver ? "#dc2626" : "#d97706",
                      }}
                    >
                      {alert.isOver ? "❌ Over Budget" : "⚠️ Near Limit"} (
                      {alert.pct.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={isSupervisor ? 4 : 3}>
          <StatCard
            title={isSupervisor ? "My Sites" : "Total Sites"}
            value={stats?.sites?.total || 0}
            icon={AccountBalance}
            color="#1F8F6E"
            subtitle={`${stats?.sites?.active || 0} active`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={isSupervisor ? 4 : 3}>
          <StatCard
            title="Material Entries"
            value={stats?.materials?.totalEntries || 0}
            icon={Inventory}
            color="#C6791E"
            subtitle="Total transactions"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={isSupervisor ? 4 : 3}>
          <StatCard
            title="Total Spending"
            value={`₹${((stats?.spending?.total || 0) / 10000000).toFixed(2)}Cr`}
            icon={TrendingUp}
            color="#4F46E5"
            subtitle={isSupervisor ? "In your sites" : "Across all sites"}
          />
        </Grid>
        {!isSupervisor && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Budget Alerts"
              value={budgetAlerts.length}
              icon={WarningAmber}
              color={budgetAlerts.some((a) => a.isOver) ? "#dc2626" : "#d97706"}
              subtitle={
                budgetAlerts.length === 0
                  ? "All sites within budget"
                  : `${budgetAlerts.filter((a) => a.isOver).length} over budget`
              }
            />
          </Grid>
        )}
      </Grid>

      {/* Final BOQs Section — Admin/Manager only */}
      {!isSupervisor && finalBoqs.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                ⭐ Final BOQs
              </Typography>
              <Chip
                label={`${finalBoqs.length} sites`}
                size="small"
                color="warning"
              />
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Site</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Approved By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Final Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {finalBoqs.slice(0, 10).map((boq) => (
                  <TableRow key={boq.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {boq.site?.siteName || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {boq.site?.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {boq.versionLabel}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          boq._source === "multi" ? "Multi-Sheet" : "Single"
                        }
                        size="small"
                        color={boq._source === "multi" ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {boq.approver?.name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="warning.dark"
                      >
                        {boq._source === "multi"
                          ? (() => {
                            const s = (boq.sheets || []).find(
                              (s) =>
                                s.totalUnit === "₹" &&
                                parseFloat(s.totalAmount) > 0,
                            );
                            return s
                              ? `₹${Number(parseFloat(s.totalAmount)).toLocaleString("en-IN")}`
                              : "—";
                          })()
                          : `₹${Number(boq.totalAmount || 0).toLocaleString("en-IN")}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {finalBoqs.length > 10 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="caption" color="text.secondary">
                        Showing 10 of {finalBoqs.length} sites — go to BOQ
                        Management to view all
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Spending by Department - Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              height: 400,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <PieChartOutline sx={{ color: "#1F8F6E" }} />
              <Typography variant="h6" fontWeight="bold">
                Spending by Department
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={departmentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ display }) => display}
                >
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => props.payload.display}
                />
                <Legend
                  formatter={(value, entry) => {
                    const total = totalCivilSpending + totalElectricalSpending;
                    const pct =
                      total > 0
                        ? ((entry.payload.value / total) * 100).toFixed(1)
                        : "0";
                    return `${value} (${pct}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Spending by Site - Bar Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 3,
              height: 400,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <BarChartIcon sx={{ color: "#1F8F6E" }} />
              <Typography variant="h6" fontWeight="bold">
                Spending by Site (in Lakhs)
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={siteSpendingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  style={{ fontSize: "0.75rem" }}
                />
                <YAxis
                  label={{
                    value: "₹ (Lakhs)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                  style={{ fontSize: "0.75rem" }}
                />
                <Tooltip
                  formatter={(value) => [
                    `₹${(value * 100000).toLocaleString()}`,
                    "Spending",
                  ]}
                  labelFormatter={(label) => {
                    const site = siteSpendingData.find((s) => s.name === label);
                    return site?.fullName || label;
                  }}
                />
                <Legend />
                <Bar
                  dataKey="spending"
                  fill="#1F8F6E"
                  radius={[8, 8, 0, 0]}
                  name="Spending"
                >
                  <LabelList
                    dataKey="spending"
                    position="top"
                    formatter={(v) => `₹${v.toFixed(1)}L`}
                    style={{ fontSize: 11, fill: "#555" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Top 10 Materials by Department */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Category sx={{ color: "text.secondary" }} />
            <Typography variant="h6" fontWeight="bold">
              Top 10 Materials by Department
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search material..."
              value={materialSearch}
              onChange={(e) => setMaterialSearch(e.target.value)}
              sx={{ width: 180 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: materialSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setMaterialSearch("")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Filter by Site"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Sites</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.siteName}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Civil Department */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: "2px solid #1F8F6E",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 2, color: "#1F8F6E" }}
              >
                Civil Department ({topMaterials.civil.length} materials)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Spent
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCivil.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            No materials found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCivil.map((material, index) => (
                        <TableRow
                          key={index}
                          sx={{ "&:hover": { bgcolor: "action.hover" } }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {material.materialName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {material.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {material.totalQuantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="#1F8F6E"
                            >
                              ₹{material.totalSpent.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>

          {/* Electrical Department */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: "2px solid #C4472C",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 2, color: "#C4472C" }}
              >
                Electrical Department ({topMaterials.electrical.length}{" "}
                materials)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Spent
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredElectrical.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            No materials found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredElectrical.map((material, index) => (
                        <TableRow
                          key={index}
                          sx={{ "&:hover": { bgcolor: "action.hover" } }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {material.materialName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {material.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {material.totalQuantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="#C4472C"
                            >
                              ₹{material.totalSpent.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Activities */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <History sx={{ color: "text.secondary" }} />
            <Typography variant="h6" fontWeight="bold">
              Recent Activities
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search activities..."
            value={activitySearch}
            onChange={(e) => setActivitySearch(e.target.value)}
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: activitySearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setActivitySearch("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
                {filteredActivities.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No recent activities
          </Typography>
        ) : (
          <Box>
            {filteredActivities.map((activity, index) => (
              <Box
                key={activity.id || index}
                sx={{
                  display: "flex",
                  gap: 1.75,
                  py: 1.5,
                  borderTop: index === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                <FiberManualRecord
                  sx={{
                    fontSize: 10,
                    color: getActivityColor(activity.type || activity.action),
                    mt: 0.5,
                  }}
                />
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="body2">
                      <strong>{activity.type || activity.action || "Material Entry"}</strong>
                      {" — "}
                      {activity.materialName || "N/A"} at {activity.siteName || "N/A"}
                      {activity.quantity ? ` (${activity.quantity} ${activity.unit || ""})` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(activity.createdAt || activity.date).toLocaleDateString("en-IN", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="primary" sx={{ whiteSpace: "nowrap" }}>
                    ₹{(activity.amount || activity.totalAmount || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      </>
      )}

      {activeTab === "sites" && (
        <Grid container spacing={3}>
          {sites.map((site) => {
            const spending = parseFloat(site.actualSpending) || 0;
            const budget = parseFloat(site.projectedBudget) || 0;
            const pct = budget > 0 ? (spending / budget) * 100 : 0;
            const isOver = pct >= 100;
            const isNear = !isOver && pct >= (parseFloat(site.budgetAlertThreshold) || 80);
            const ringColor = isOver ? "#C4472C" : isNear ? "#C6791E" : "#1F8F6E";
            const statusLabel = isOver ? "Over budget" : isNear ? "Near limit" : "Healthy";
            const statusBg = isOver ? "#FBE7E2" : isNear ? "#FBEEDD" : "#E4F3EE";

            return (
              <Grid item xs={12} sm={6} md={4} key={site.id}>
                <Card
                  onClick={() => navigate(`/sites/${site.id}`)}
                  sx={{
                    p: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: 52,
                        height: 52,
                        minWidth: 52,
                        borderRadius: "50%",
                        background: `conic-gradient(${ringColor} 0% ${Math.min(pct, 100)}%, #F0ECE0 ${Math.min(pct, 100)}% 100%)`,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          inset: "6px",
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.65rem",
                          fontWeight: 800,
                        }}
                      >
                        {pct.toFixed(0)}%
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {site.siteName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {site.location}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ₹{spending.toLocaleString("en-IN")} / ₹{budget.toLocaleString("en-IN")}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: ringColor,
                      bgcolor: statusBg,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 999,
                    }}
                  >
                    {statusLabel}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default DashboardHome;
